// Curated font themes shared across the flyer, public pages, and pickers.
// `display` is the heading font stack, `body` is the body font stack (real
// Google Fonts). `googleFamily` is the css2 query used to lazy-load that theme's
// fonts on demand. Keep IDs in sync with backend/fonts.py.

export const FONT_THEMES = [
  // --- Clean & modern sans ---
  {
    id: 'modern',
    name: 'Modern Clean',
    desc: 'Geometric & contemporary',
    display: "'Montserrat', Arial, sans-serif",
    body: "'Inter', Arial, sans-serif",
    googleFamily: 'family=Montserrat:wght@500;600;700&family=Inter:wght@400;500;600',
  },
  {
    id: 'soft',
    name: 'Soft Geometric',
    desc: 'Friendly, balanced, modern',
    display: "'Poppins', Arial, sans-serif",
    body: "'Work Sans', Arial, sans-serif",
    googleFamily: 'family=Poppins:wght@500;600;700&family=Work+Sans:wght@400;500;600',
  },
  {
    id: 'warm',
    name: 'Warm & Friendly',
    desc: 'Rounded and approachable',
    display: "'Fredoka', 'Trebuchet MS', sans-serif",
    body: "'Nunito', 'Trebuchet MS', sans-serif",
    googleFamily: 'family=Fredoka:wght@500;600;700&family=Nunito:wght@400;600;700',
  },
  {
    id: 'playful',
    name: 'Playful Rounded',
    desc: 'Bouncy rounded sans',
    display: "'Quicksand', 'Trebuchet MS', sans-serif",
    body: "'Karla', Arial, sans-serif",
    googleFamily: 'family=Quicksand:wght@500;600;700&family=Karla:wght@400;500;600',
  },
  // --- Strong / poster ---
  {
    id: 'bold',
    name: 'Bold Condensed',
    desc: 'Tall, narrow, punchy',
    display: "'Oswald', 'Arial Narrow', sans-serif",
    body: "'Lato', Arial, sans-serif",
    googleFamily: 'family=Oswald:wght@500;600;700&family=Lato:wght@400;700',
  },
  {
    id: 'statement',
    name: 'Statement Impact',
    desc: 'Heavy all-caps poster',
    display: "'Anton', 'Arial Narrow', sans-serif",
    body: "'Roboto', Arial, sans-serif",
    googleFamily: 'family=Anton&family=Roboto:wght@400;500;700',
  },
  // --- Serif family ---
  {
    id: 'editorial',
    name: 'Editorial',
    desc: 'Expressive serif + grotesk',
    display: "'Fraunces', ui-serif, Georgia, serif",
    body: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
    googleFamily: 'family=Fraunces:opsz,wght@9..144,500,600,700&family=Space+Grotesk:wght@400;500;600;700',
  },
  {
    id: 'classic',
    name: 'Classic Elegant',
    desc: 'High-contrast Playfair',
    display: "'Playfair Display', Georgia, serif",
    body: "'Source Sans 3', Arial, sans-serif",
    googleFamily: 'family=Playfair+Display:wght@500;600;700&family=Source+Sans+3:wght@400;500;600',
  },
  {
    id: 'timeless',
    name: 'Timeless Serif',
    desc: 'Literary book serif',
    display: "'Crimson Text', Georgia, serif",
    body: "'Lato', Arial, sans-serif",
    googleFamily: 'family=Crimson+Text:wght@400;600;700&family=Lato:wght@400;700',
  },
  {
    id: 'refined',
    name: 'Refined',
    desc: 'Delicate high-contrast serif',
    display: "'Cormorant Garamond', Georgia, serif",
    body: "'Mulish', Arial, sans-serif",
    googleFamily: 'family=Cormorant+Garamond:wght@500;600;700&family=Mulish:wght@400;500;600',
  },
  {
    id: 'slab',
    name: 'Sturdy Slab',
    desc: 'Solid mechanical slab serif',
    display: "'Roboto Slab', Georgia, serif",
    body: "'Roboto', Arial, sans-serif",
    googleFamily: 'family=Roboto+Slab:wght@500;600;700&family=Roboto:wght@400;500;700',
  },
  // --- Display / luxe ---
  {
    id: 'deco',
    name: 'Art Deco',
    desc: 'Luxe engraved capitals',
    display: "'Cinzel', Georgia, serif",
    body: "'Montserrat', Arial, sans-serif",
    googleFamily: 'family=Cinzel:wght@500;600;700&family=Montserrat:wght@400;500;600',
  },
  {
    id: 'vintage',
    name: 'Vintage Poster',
    desc: 'Dramatic fat display serif',
    display: "'Abril Fatface', Georgia, serif",
    body: "'Lato', Arial, sans-serif",
    googleFamily: 'family=Abril+Fatface&family=Lato:wght@400;700',
  },
  // --- Script / handwritten ---
  {
    id: 'romantic',
    name: 'Romantic Script',
    desc: 'Flowing wedding script',
    display: "'Dancing Script', cursive",
    body: "'Lato', Arial, sans-serif",
    googleFamily: 'family=Dancing+Script:wght@500;600;700&family=Lato:wght@400;700',
  },
  {
    id: 'whimsical',
    name: 'Whimsical',
    desc: 'Fun retro brush script',
    display: "'Pacifico', cursive",
    body: "'Nunito', 'Trebuchet MS', sans-serif",
    googleFamily: 'family=Pacifico&family=Nunito:wght@400;600;700',
  },
  {
    id: 'handwritten',
    name: 'Handwritten',
    desc: 'Casual marker handwriting',
    display: "'Caveat', cursive",
    body: "'Mulish', Arial, sans-serif",
    googleFamily: 'family=Caveat:wght@500;600;700&family=Mulish:wght@400;500;600',
  },
  {
    id: 'retro',
    name: 'Retro Diner',
    desc: 'Bold retro signage script',
    display: "'Lobster', cursive",
    body: "'Roboto', Arial, sans-serif",
    googleFamily: 'family=Lobster&family=Roboto:wght@400;500;700',
  },
  // --- Typewriter / mono ---
  {
    id: 'typewriter',
    name: 'Typewriter',
    desc: 'Monospaced typewriter feel',
    display: "'Space Mono', 'Courier New', monospace",
    body: "'IBM Plex Mono', 'Courier New', monospace",
    googleFamily: 'family=Space+Mono:wght@400;700&family=IBM+Plex+Mono:wght@400;500',
  },
];

export const DEFAULT_FONT_THEME = 'editorial';

const BY_ID = FONT_THEMES.reduce((acc, t) => {
  acc[t.id] = t;
  return acc;
}, {});

export const getFontTheme = (id) => BY_ID[id] || BY_ID[DEFAULT_FONT_THEME];

// --- On-demand Google Fonts loading -------------------------------------
// Injects a <link> for a theme's fonts only when needed, so public pages load
// just the selected font instead of all ~18 families.
const LINK_PREFIX = 'tg-font-';

export const ensureFontLoaded = (id) => {
  if (typeof document === 'undefined') return;
  const t = BY_ID[id] || BY_ID[DEFAULT_FONT_THEME];
  if (!t || !t.googleFamily) return;
  const linkId = LINK_PREFIX + t.id;
  if (document.getElementById(linkId)) return;
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${t.googleFamily}&display=swap`;
  document.head.appendChild(link);
};

// Loads every theme's fonts — used on the picker screens so all previews render.
export const ensureAllFontsLoaded = () => {
  FONT_THEMES.forEach((t) => ensureFontLoaded(t.id));
};
